import React from "react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import PortalClosedLostModal from "./PortalClosedLostModal";
import PortalCreateTaskDrawer from "./PortalCreateTaskDrawer";
import Modal from "../ui/Modal";
import PortalNextStageCTA from "./PortalNextStageCTA";
import PortalKycHeatIndicator from "./PortalKycHeatIndicator";
import { formatDateAsDaysAgo } from "../../utilities/daysFromDate";
import format from "date-fns/format";
import { formatLegDepTimeDisplay } from "../../utilities/formatLegDepTimeDisplay";
import arrowLeft from "../../assets/icons/arrow-left.svg";
import tickIconGreen from "../../assets/icons/tick-icon-green.svg";
import crossIconRed from "../../assets/icons/cross-icon-red.svg";
import questionIcon from "../../assets/icons/question-icon.svg";
import refreshCw from "../../assets/icons/refresh-cw.svg";
import onewayPortal from "../../assets/icons/oneway-portal.png";
import multilegPortal from "../../assets/icons/multileg-portal.png";

const formatLegDate = (d) => {
    if (!d) return "-";
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d.trim())) {
        const [y, m, day] = d.trim().split("-");
        return `${day}/${m}/${y.slice(2)}`;
    }
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return format(dt, "dd/MM/yy", { awareOfUnicodeTokens: true });
};

const PortalDetailsHeader = ({ deal }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = location.state?.returnTo;
    const [closedLostShy, setClosedLostShy] = useState(false);
    const [closedLostClient, setClosedLostClient] = useState(false);
    const [createTaskOpen, setCreateTaskOpen] = useState(false);

    const updatedDate = new Date(deal.modified_Time);
    const daysAgo = formatDateAsDaysAgo(updatedDate);

    const legsCount = deal.legs ? deal.legs.length : 0;
    const isOneWay = legsCount === 1;
    const isReturn = legsCount === 2;
    const isMultiLeg = legsCount > 2;

    const onClosedLostShy = () => {
        setClosedLostShy(true);
    };

    const onClosedLostClient = () => {
        setClosedLostClient(true);
    };

    return (
        <>
            <div className={"portal__deal_details_header"}>
                {/* Top row: BACK button on left, action buttons on right */}
                <div className={"deal_details_header__top_row"}>
                    <Link
                        to="#"
                        onClick={(event) => {
                            event.preventDefault();
                            if (returnTo) {
                                navigate(returnTo);
                            } else {
                                navigate(-1);
                            }
                        }}
                        className={"deal_details_header__back_link"}
                    >
                        <img src={arrowLeft} alt="" />
                        <span>BACK</span>
                    </Link>
                    <div className={"deal_details_header__top_buttons"}>
                        <PortalNextStageCTA deal={deal} />
                        <Button classNames={["button button--secondary"]} onClick={onClosedLostShy}>
                            Closed/Lost (Shy)
                        </Button>
                        <Button classNames={["button button--secondary"]} onClick={onClosedLostClient}>
                            Closed/Lost (client)
                        </Button>
                    </div>
                </div>

                {/* Title row: Deal name, Updated text, KYC tag */}
                {deal && (
                    <div className={"deal_details_header__title_row"}>
                        <div className={"deal_details_header__title_wrapper"}>
                            <h2 className={"deal_details_header__title"}>
                                {isOneWay && (
                                    <img src={onewayPortal} alt="" className={"deal_details_header__title_icon"} />
                                )}
                                {isReturn && (
                                    <img src={refreshCw} alt="" className={"deal_details_header__title_icon"} />
                                )}
                                {isMultiLeg && (
                                    <img src={multilegPortal} alt="" className={"deal_details_header__title_icon"} />
                                )}
                                {deal.deal_Name}
                            </h2>
                        </div>
                        <div className={"deal_details_header__title_meta"}>
                            <span className={"deal_details_header__updated"}>UPDATED: {daysAgo.toUpperCase()}</span>
                            {deal.kyC_Heat && (
                                <div className={"deal_details_header__kyc_tag"}>
                                    <PortalKycHeatIndicator kycHeat={deal.kyC_Heat} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className={"gold_divider"}></div>

                {/* Two-column layout for deal details */}
                {deal && (
                    <div className={"deal_details_header__info_grid"}>
                        <div className={"deal_details_header__info_column"}>
                            <div className={"deal_details_header__info_item"}>
                                <label>{deal.leadFullName.trim().length ? "Lead" : "Client"} Name:</label>{" "}
                                {deal.leadFullName.trim().length ? deal.leadFullName : deal.contactFullName}
                            </div>
                            {deal.tenantID && (
                                <div className={"deal_details_header__info_item"}>
                                    <label>Tenant ID:</label> {deal.tenantID}
                                </div>
                            )}
                            <div className={"deal_details_header__info_item"}>
                                <label>Deal Owner:</label> {deal.ownerFullName}
                            </div>
                            <div className={"deal_details_header__info_item"}>
                                <label>Deal Stage:</label> {deal.stage}
                            </div>
                            <div className={"deal_details_header__create_task"}>
                                <Button
                                    classNames={["button button--secondary"]}
                                    onClick={() => setCreateTaskOpen(true)}
                                >
                                    + Create Task
                                </Button>
                            </div>
                            <div className={"deal_details_header__mobile_buttons"}>
                                <PortalNextStageCTA deal={deal} />
                                <Button classNames={["button button--secondary"]} onClick={onClosedLostShy}>
                                    Closed/Lost (Shy)
                                </Button>
                                <Button classNames={["button button--secondary"]} onClick={onClosedLostClient}>
                                    Closed/Lost (client)
                                </Button>
                            </div>
                        </div>
                        {deal.legs && (
                            <div className={"deal_card_info_column deal_card_info_column--route"}>
                                {deal.legs
                                    .sort((a, b) => {
                                        return a.legNumber < b.legNumber ? -1 : 1;
                                    })
                                    .map((leg, index) => (
                                        <p key={index} className={"deal_card_info_item deal_card_leg-row"}>
                                            <span className={"deal_card_leg-route"}>
                                                <strong>Leg {index + 1}:</strong> {leg.legFrom} - {leg.legTo}
                                            </span>
                                            <span className={"deal_card_leg-date"}>
                                                <strong>Dep Date:</strong> {formatLegDate(leg.legDate)}
                                            </span>
                                            <span className={"deal_card_leg-time"}>
                                                <strong>Dep Time:</strong> {formatLegDepTimeDisplay(leg.legTime)}
                                            </span>
                                        </p>
                                    ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Trip ID and status indicators */}
                <div className={"deal_details_header__status_row"}>
                    {deal.avinodeTripId && (
                        <span className={"deal_details_header__trip_id"}>TRIP ID: {deal.avinodeTripId}</span>
                    )}
                    {deal.quoteStats && (
                        <>
                            <div className={"deal_details_header__status_item"}>
                                <img src={tickIconGreen} alt="" />
                                <span>{deal.quoteStats.accepted}</span>
                            </div>
                            <div className={"deal_details_header__status_item"}>
                                <img src={crossIconRed} alt="" />
                                <span>{deal.quoteStats.declined}</span>
                            </div>
                            <div className={"deal_details_header__status_item"}>
                                <img src={questionIcon} alt="" />
                                <span>{deal.quoteStats.unanswered}</span>
                            </div>
                        </>
                    )}
                    {deal.ownerFullName && (
                        <span className={"deal_details_header__avinode_user"}>
                            AVINODE USER: {deal.ownerFullName.toUpperCase()}
                        </span>
                    )}
                </div>
            </div>

            <Modal
                classNames={["modal__content--thin modal__content--overlap"]}
                modalVisible={closedLostShy}
                closeModal={() => setClosedLostShy(false)}
            >
                <PortalClosedLostModal
                    closeModal={() => setClosedLostShy(false)}
                    closedBy={"shy"}
                    dealId={deal.id}
                    initialKycHeat={deal.kyC_Heat}
                />
            </Modal>

            <Modal
                classNames={["modal__content--thin modal__content--overlap"]}
                modalVisible={closedLostClient}
                closeModal={() => setClosedLostClient(false)}
            >
                <PortalClosedLostModal
                    closeModal={() => setClosedLostClient(false)}
                    closedBy={"client"}
                    dealId={deal.id}
                    initialKycHeat={deal.kyC_Heat}
                />
            </Modal>

            <PortalCreateTaskDrawer open={createTaskOpen} onClose={() => setCreateTaskOpen(false)} deal={deal} />
        </>
    );
};

export default PortalDetailsHeader;
